import {
  Box,
  HeadingLarge,
  HeadingMedium,
  TabItem,
  Breadcrumb,
  DeprecatedChevronDownIcon,
  DeprecatedChevronUpIcon,
} from '@arnold/common';
import { FC, useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { PageWrapper } from '../Common';
import { Loading } from '../index';
import {
  Features,
  TopicGroupTypeCode,
  useAllowedTopicGroupsQuery,
  useOrganizationQuery,
  UserQuery,
  AllowedTopicGroupsQuery,
} from '../../generated/hooks';
import { NewCustomTopicButton } from './components/NewCustomTopicButton';
import { CategoryIcon } from './components/CategoryIcons';
import { TopicGroup } from './components/TopicGroup';
import { TopicsFilters, TopicType, SortOption, NOT_RUN_STATUS } from './components/TopicsFilters';
import {
  Pill,
  StickyFilterHeader,
  FilterLabel,
  StickyFilterContainer,
  StickyFilterContent,
} from './components/TopicsFiltersStyled';
import { ProductCode } from '@arnold/core/lib/enum';

export { SortOption } from './components/TopicsFilters';

type TopicGroupType = NonNullable<AllowedTopicGroupsQuery['allowedTopicGroups']>[0];

// Category order priority list
const CATEGORY_ORDER = [
  'intro',
  'onboard',
  'offboard',
  'attr',
  'hiring',
  'reten',
  'motiv',
  'change',
  'prod',
  'succ',
  'culture',
  'devel',
  'remote',
];

const getCategoryOrder = (categoryCode: string | null | undefined): number => {
  if (!categoryCode) {
    return CATEGORY_ORDER.length + 1; // Uncategorized goes after all ordered categories
  }
  const index = CATEGORY_ORDER.indexOf(categoryCode);
  return index === -1 ? CATEGORY_ORDER.length + 1 : index; // If not in list, goes after ordered categories
};

type TopicsProps = {
  user: UserQuery['user'];
  heading: string;
  breadcrumbItems?: TabItem[];
  hideCreateTopicButton?: boolean;
  dataIcom?: string;
  hideCategoriesInFilter?: boolean;
  includeStepTopics?: boolean;
  defaultSortOption?: SortOption;
  actionButtonText?: string;
  actionButtonOnClick?: (topicGroup: TopicGroupType) => void;
  requireValidForAction?: boolean;
  filterTopicGroups?: (topicGroup: TopicGroupType) => boolean;
  createTopicTypeCode?: TopicGroupTypeCode;
};

const parseSetFromParam = (param: string | null): Set<string> => {
  if (!param) return new Set();
  return new Set(param.split(',').filter(Boolean));
};

const serializeSetToParam = (set: Set<string>): string | null => {
  if (set.size === 0) return null;
  return Array.from(set).join(',');
};

export const Topics: FC<TopicsProps> = ({
  heading,
  breadcrumbItems,
  hideCreateTopicButton,
  dataIcom,
  user,
  hideCategoriesInFilter,
  defaultSortOption = SortOption.GROUP_BY_CATEGORY,
  actionButtonText,
  includeStepTopics = false,
  actionButtonOnClick,
  requireValidForAction,
  filterTopicGroups,
  createTopicTypeCode,
}) => {
  const { t, i18n } = useTranslation('topics');
  const history = useHistory();
  const location = useLocation();

  const getInitialState = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return {
      topicTypes: parseSetFromParam(params.get('types')) as Set<TopicType>,
      search: params.get('search') || '',
      sort: (params.get('sort') as SortOption) || defaultSortOption,
      categories: parseSetFromParam(params.get('categories')),
      statuses: parseSetFromParam(params.get('statuses')),
    };
  }, [location.search, defaultSortOption]);

  const initialState = getInitialState();

  const [selectedTopicTypes, setSelectedTopicTypes] = useState<Set<TopicType>>(initialState.topicTypes);
  const [searchQuery, setSearchQuery] = useState(initialState.search);
  const [sortOption, setSortOption] = useState<SortOption>(initialState.sort);
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(initialState.categories);
  const [selectedSurveyStatuses, setSelectedSurveyStatuses] = useState<Set<string>>(initialState.statuses);
  const [showStickyFilterBar, setShowStickyFilterBar] = useState(false);
  const [isStickyFilterExpanded, setIsStickyFilterExpanded] = useState(false);
  const filterSectionRef = useRef<HTMLDivElement>(null);
  const [topicActionLoading, setTopicActionLoading] = useState(false);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();

    const typesValue = serializeSetToParam(selectedTopicTypes);
    if (typesValue) params.set('types', typesValue);

    if (searchQuery) params.set('search', searchQuery);

    if (sortOption !== defaultSortOption) params.set('sort', sortOption);
    const categoriesValue = serializeSetToParam(selectedCategories);
    if (categoriesValue) params.set('categories', categoriesValue);

    const statusesValue = serializeSetToParam(selectedSurveyStatuses);
    if (statusesValue) params.set('statuses', statusesValue);

    const newSearch = params.toString();
    const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

    if (newUrl !== location.pathname + location.search) {
      history.replace(newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTopicTypes,
    searchQuery,
    sortOption,
    selectedCategories,
    selectedSurveyStatuses,
    location.pathname,
    history,
    defaultSortOption,
  ]);

  const { loading, data, refetch } = useAllowedTopicGroupsQuery({
    variables: {
      languageCode: i18n.language,
    },
  });

  // organization is most probably loaded already by the authorizedPageLoader, this is just to prevent prop drilling
  const { data: organizationData, loading: organizationLoading } = useOrganizationQuery();

  useEffect(() => {
    refetch({ languageCode: i18n.language });
  }, [i18n.language, refetch]);

  useEffect(() => {
    const handleScroll = () => {
      if (!filterSectionRef.current) {
        return;
      }
      const rect = filterSectionRef.current.getBoundingClientRect();
      const shouldShow = rect.bottom < 0;
      setShowStickyFilterBar(shouldShow);
      // Collapse when scrolling back up (when filter section becomes visible again)
      if (!shouldShow) {
        setIsStickyFilterExpanded(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const organization = organizationData?.organization;

  if (loading || organizationLoading || !organization) {
    return <Loading />;
  }

  const handleStickyFilterBarClick = () => {
    setIsStickyFilterExpanded(!isStickyFilterExpanded);
  };

  const handleTopicTypeToggle = (type: TopicType) => {
    const newSelected = new Set(selectedTopicTypes);
    if (newSelected.has(type)) {
      newSelected.delete(type);
    } else {
      newSelected.add(type);
    }
    setSelectedTopicTypes(newSelected);
  };

  const handleCategoryToggle = (category: string) => {
    const newSelected = new Set(selectedCategories);
    if (newSelected.has(category)) {
      newSelected.delete(category);
    } else {
      newSelected.add(category);
    }
    setSelectedCategories(newSelected);
  };

  const handleStatusToggle = (status: string) => {
    const newSelected = new Set(selectedSurveyStatuses);
    if (newSelected.has(status)) {
      newSelected.delete(status);
    } else {
      newSelected.add(status);
    }
    setSelectedSurveyStatuses(newSelected);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleSortChange = (value: SortOption) => {
    setSortOption(value);
  };

  const handleClearTopicTypes = () => {
    setSelectedTopicTypes(new Set());
  };

  const handleClearCategories = () => {
    setSelectedCategories(new Set());
  };

  const handleClearStatuses = () => {
    setSelectedSurveyStatuses(new Set());
  };

  // Create map of category codes to category names (from all topics, not just filtered)
  const categoryCodesToNames = (data?.allowedTopicGroups || []).reduce(
    (acc, topic) => {
      const category: string = topic.categoryCode ?? 'uncategorized';
      if (!acc[category]) {
        acc[category] = topic.categoryName ?? t('uncategorized');
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  // Helper function to get survey status from lastSurvey or lastSurveyGroup
  const getSurveyStatus = (
    topic: NonNullable<AllowedTopicGroupsQuery['allowedTopicGroups']>[number],
  ): string | null => {
    // For process surveys (with steps), use lastSurveyGroup status
    if (topic.containsSteps && topic.lastSurveyGroup?.status) {
      return topic.lastSurveyGroup.status;
    }
    // For regular surveys, use lastSurvey status
    if (topic.lastSurvey?.status) {
      return topic.lastSurvey.status;
    }
    return null;
  };

  // Collect all stepTopicGroup IDs to filter them out
  const stepTopicGroupIds = includeStepTopics
    ? new Set()
    : new Set(
        (data?.allowedTopicGroups || [])
          .flatMap((topicGroup) => topicGroup.steps?.map((step) => step.stepTopicGroup?.id).filter(Boolean) || [])
          .map((id) => id?.toString()),
      );

  // Create a map of topic ID to topic for efficient step topic lookup
  const topicByIdMap = new Map((data?.allowedTopicGroups || []).map((topic) => [topic.id, topic]));

  // Filter topics by topic type and survey status (but not by category yet)
  const topicsFilteredByTypeAndStatus =
    data?.allowedTopicGroups?.filter((topic) => {
      // Filter out topic groups that are used as steps in other processes
      if (stepTopicGroupIds.has(topic.id?.toString())) {
        return false;
      }

      // Apply custom filter if provided
      if (filterTopicGroups && !filterTopicGroups(topic)) {
        return false;
      }

      // Filter by topic type (when nothing selected, show all)
      if (selectedTopicTypes.size > 0) {
        const isCustomTopic = topic.organization?.id === organization.id;
        const topicType = isCustomTopic ? TopicType.CUSTOM : TopicType.SYSTEM;
        if (!selectedTopicTypes.has(topicType)) {
          return false;
        }
      }

      // Filter by selected survey statuses
      if (selectedSurveyStatuses.size > 0) {
        const surveyStatus = getSurveyStatus(topic);
        // If topic has no status (never run), include only if NOT_RUN is selected
        if (!surveyStatus) {
          return selectedSurveyStatuses.has(NOT_RUN_STATUS);
        }
        // If topic has a status, include only if that status is selected
        return selectedSurveyStatuses.has(surveyStatus);
      }

      return true;
    }) ?? [];

  // Get all unique categories from all topics (excluding step topics and applying custom filter), not affected by other filters
  const allTopicsExcludingSteps =
    data?.allowedTopicGroups?.filter(
      (topic) => !stepTopicGroupIds.has(topic.id?.toString()) && (!filterTopicGroups || filterTopicGroups(topic)),
    ) ?? [];

  const categoriesWithCode = Array.from(
    new Set(allTopicsExcludingSteps.map((topic) => topic.categoryCode).filter((code): code is string => !!code)),
  );

  // Check if there are any uncategorized topics
  const hasUncategorized = allTopicsExcludingSteps.some((topic) => !topic.categoryCode);

  // Add uncategorized to the list if it exists, then sort
  const availableCategories = [...categoriesWithCode, ...(hasUncategorized ? ['uncategorized'] : [])].sort((a, b) => {
    // If one is uncategorized, it goes last
    if (a === 'uncategorized') return 1;
    if (b === 'uncategorized') return -1;
    const orderA = getCategoryOrder(a);
    const orderB = getCategoryOrder(b);
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return a.localeCompare(b);
  });

  // Helper function to check if a topic matches the search query
  const topicMatchesSearch = (
    topic: NonNullable<AllowedTopicGroupsQuery['allowedTopicGroups']>[number],
    query: string,
  ): boolean => {
    if (!query.trim()) {
      return true; // Empty query returns all topics
    }

    const normalizedQuery = query.toLowerCase().trim();

    // Check topic name
    if (topic.name?.toLowerCase().includes(normalizedQuery)) {
      return true;
    }

    // Check category name
    if (topic.categoryName?.toLowerCase().includes(normalizedQuery)) {
      return true;
    }

    // Check step topic names
    if (topic.steps && topic.steps.length > 0) {
      const stepTopicGroupIds = topic.steps.map((step) => step.stepTopicGroup?.id).filter(Boolean);

      const stepTopics = stepTopicGroupIds
        .map((id) => topicByIdMap.get(id))
        .filter((t): t is NonNullable<typeof t> => t !== undefined);

      if (stepTopics.some((stepTopic) => stepTopic.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }
    }

    return false;
  };

  // Calculate counts for filter options
  const uncategorizedLabel = 'uncategorized';

  const topicTypeCounts = allTopicsExcludingSteps.reduce(
    (acc, topic) => {
      const isCustomTopic = topic.organization?.id === organization.id;
      const topicType = isCustomTopic ? TopicType.CUSTOM : TopicType.SYSTEM;

      // Check if topic matches current status, category filters, and search
      const matchesStatus =
        selectedSurveyStatuses.size === 0 ||
        (() => {
          const surveyStatus = getSurveyStatus(topic);
          if (!surveyStatus) return selectedSurveyStatuses.has(NOT_RUN_STATUS);
          return selectedSurveyStatuses.has(surveyStatus);
        })();

      const matchesCategory =
        selectedCategories.size === 0 || selectedCategories.has(topic.categoryCode || uncategorizedLabel);

      const matchesSearch = topicMatchesSearch(topic, searchQuery);

      if (matchesStatus && matchesCategory && matchesSearch) {
        acc[topicType] = (acc[topicType] || 0) + 1;
      }
      return acc;
    },
    {} as Record<TopicType, number>,
  );

  const statusCounts = allTopicsExcludingSteps.reduce(
    (acc, topic) => {
      const surveyStatus = getSurveyStatus(topic);
      const statusKey = surveyStatus || NOT_RUN_STATUS;

      // Check if topic matches current type, category filters, and search
      const matchesType =
        selectedTopicTypes.size === 0 ||
        (() => {
          const isCustomTopic = topic.organization?.id === organization.id;
          const topicType = isCustomTopic ? TopicType.CUSTOM : TopicType.SYSTEM;
          return selectedTopicTypes.has(topicType);
        })();

      const matchesCategory =
        selectedCategories.size === 0 || selectedCategories.has(topic.categoryCode || uncategorizedLabel);

      const matchesSearch = topicMatchesSearch(topic, searchQuery);

      if (matchesType && matchesCategory && matchesSearch) {
        acc[statusKey] = (acc[statusKey] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  const categoryCounts = allTopicsExcludingSteps.reduce(
    (acc, topic) => {
      const categoryCode: string = topic.categoryCode || uncategorizedLabel;

      // Check if topic matches current type, status filters, and search
      const matchesType =
        selectedTopicTypes.size === 0 ||
        (() => {
          const isCustomTopic = topic.organization?.id === organization.id;
          const topicType = isCustomTopic ? TopicType.CUSTOM : TopicType.SYSTEM;
          return selectedTopicTypes.has(topicType);
        })();

      const matchesStatus =
        selectedSurveyStatuses.size === 0 ||
        (() => {
          const surveyStatus = getSurveyStatus(topic);
          if (!surveyStatus) return selectedSurveyStatuses.has(NOT_RUN_STATUS);
          return selectedSurveyStatuses.has(surveyStatus);
        })();

      const matchesSearch = topicMatchesSearch(topic, searchQuery);

      if (matchesType && matchesStatus && matchesSearch) {
        acc[categoryCode] = (acc[categoryCode] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  // Apply category filter and search query to get final filtered topics
  const filteredTopics = topicsFilteredByTypeAndStatus.filter((topic) => {
    // Filter by selected categories
    if (selectedCategories.size > 0) {
      const categoryCode: string = topic.categoryCode ?? 'uncategorized';
      if (!selectedCategories.has(categoryCode)) {
        return false;
      }
    }

    // Filter by search query
    if (!topicMatchesSearch(topic, searchQuery)) {
      return false;
    }

    return true;
  });

  // Helper function to get the most recent updatedAt for a topicGroup, considering step topics
  // Uses the same logic as TopicGroup.tsx (line 265)
  const getTopicGroupUpdatedAt = (topicGroup: (typeof filteredTopics)[0]): number => {
    const mainTopic = topicGroup.lastValidTopic;

    // If topicGroup has steps, find the most recently updated step topic
    if (topicGroup.containsSteps && topicGroup.steps) {
      const stepTopics = (data?.allowedTopicGroups || []).filter(
        (t) => topicGroup.steps?.some((s) => s.stepTopicGroup?.id === t.id),
      );

      const lastUpdatedStepTG = stepTopics.reduce((acc: (typeof filteredTopics)[0] | null, stepTopic) => {
        if (
          stepTopic.lastValidTopic?.updatedAt &&
          (new Date(stepTopic.lastValidTopic.updatedAt) > new Date(acc?.lastValidTopic?.updatedAt || '') || !acc)
        ) {
          return stepTopic;
        }
        return acc;
      }, null);

      // Use main topic if it exists, otherwise use the most recent step topic (same as TopicGroup.tsx line 265)
      const lastUpdatedStepTopic = mainTopic || lastUpdatedStepTG?.lastValidTopic;
      return lastUpdatedStepTopic?.updatedAt ? new Date(lastUpdatedStepTopic.updatedAt).getTime() : 0;
    }

    // For non-process surveys, just use the main topic's updatedAt
    return mainTopic?.updatedAt ? new Date(mainTopic.updatedAt).getTime() : 0;
  };

  const sortedTopics = [...filteredTopics].sort((a, b) => {
    switch (sortOption) {
      case SortOption.GROUP_BY_CATEGORY:
        // First sort by category order, then by name
        const categoryOrderA = getCategoryOrder(a.categoryCode);
        const categoryOrderB = getCategoryOrder(b.categoryCode);
        if (categoryOrderA !== categoryOrderB) {
          return categoryOrderA - categoryOrderB;
        }
        // If same category order, sort by category code, then by name
        const categoryA = a.categoryCode || '';
        const categoryB = b.categoryCode || '';
        if (categoryA !== categoryB) {
          return categoryA.localeCompare(categoryB);
        }
        return (a.name || '').localeCompare(b.name || '');
      case SortOption.UPDATED_AT_ASC:
        const updatedAtA = getTopicGroupUpdatedAt(a);
        const updatedAtB = getTopicGroupUpdatedAt(b);
        return updatedAtA - updatedAtB;
      case SortOption.UPDATED_AT_DESC:
        const updatedAtADesc = getTopicGroupUpdatedAt(a);
        const updatedAtBDesc = getTopicGroupUpdatedAt(b);
        return updatedAtBDesc - updatedAtADesc;
      case SortOption.RUN_AT_ASC:
        const runAtA = a.lastSurvey?.sendAt ? new Date(a.lastSurvey.sendAt).getTime() : 0;
        const runAtB = b.lastSurvey?.sendAt ? new Date(b.lastSurvey.sendAt).getTime() : 0;
        return runAtA - runAtB;
      case SortOption.RUN_AT_DESC:
        const runAtADesc = a.lastSurvey?.sendAt ? new Date(a.lastSurvey.sendAt).getTime() : 0;
        const runAtBDesc = b.lastSurvey?.sendAt ? new Date(b.lastSurvey.sendAt).getTime() : 0;
        return runAtBDesc - runAtADesc;
      default:
        return 0;
    }
  });

  // Group by category if that option is selected (but not when only custom topics are shown)
  const groupedByCategory =
    sortOption === SortOption.GROUP_BY_CATEGORY
      ? Object.fromEntries(
          Object.entries(
            sortedTopics.reduce(
              (acc, topic) => {
                const category: string = topic.categoryCode ?? 'uncategorized';
                if (!acc[category]) {
                  acc[category] = [];
                }
                acc[category].push(topic);
                return acc;
              },
              {} as Record<string, typeof sortedTopics>,
            ),
          ).map(([category, topics]) => [
            category,
            [...topics].sort((a, b) => {
              // First, sort by custom vs system (custom topics first)
              const aIsCustom = a.organization?.id === organization.id;
              const bIsCustom = b.organization?.id === organization.id;
              if (aIsCustom !== bIsCustom) {
                return aIsCustom ? -1 : 1; // Custom topics come first
              }
              // Then sort by updatedAt descending
              const updatedAtA = getTopicGroupUpdatedAt(a);
              const updatedAtB = getTopicGroupUpdatedAt(b);
              return updatedAtB - updatedAtA; // Descending order
            }),
          ]),
        )
      : null;

  const pillNumber = selectedTopicTypes.size + selectedCategories.size + selectedSurveyStatuses.size;
  return (
    <PageWrapper data-cy="admin-page-content">
      {breadcrumbItems && (
        <Breadcrumb classList={'mb-6'} items={[...breadcrumbItems, { title: heading, active: true }]} />
      )}
      <StickyFilterContainer style={{ display: showStickyFilterBar ? 'flex' : 'none' }}>
        <StickyFilterContent>
          <Box className="mb-0 px-16 py-6">
            <StickyFilterHeader
              onClick={handleStickyFilterBarClick}
              className={isStickyFilterExpanded ? 'mb-6' : 'mb-0'}
            >
              <Pill>{pillNumber}</Pill>
              <FilterLabel>{t('filters')}</FilterLabel>
              {isStickyFilterExpanded ? (
                <DeprecatedChevronUpIcon style={{ width: '12px', height: '12px' }} />
              ) : (
                <DeprecatedChevronDownIcon style={{ width: '12px', height: '12px' }} />
              )}
            </StickyFilterHeader>
            {isStickyFilterExpanded && (
              <TopicsFilters
                selectedTopicTypes={selectedTopicTypes}
                onTopicTypeToggle={handleTopicTypeToggle}
                onClearTopicTypes={handleClearTopicTypes}
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                sortOption={sortOption}
                onSortChange={handleSortChange}
                selectedCategories={selectedCategories}
                onCategoryToggle={handleCategoryToggle}
                onClearCategories={handleClearCategories}
                selectedSurveyStatuses={selectedSurveyStatuses}
                onStatusToggle={handleStatusToggle}
                onClearStatuses={handleClearStatuses}
                availableCategories={availableCategories}
                categoryCodesToNames={categoryCodesToNames}
                variant="sticky"
                topicTypeCounts={topicTypeCounts}
                statusCounts={statusCounts}
                categoryCounts={categoryCounts}
                hideCategoriesInFilter={hideCategoriesInFilter}
              />
            )}
          </Box>
        </StickyFilterContent>
      </StickyFilterContainer>
      <div className="mb-6 d-flex align-items-end justify-content-between">
        <HeadingLarge className="mt-0 mb-4" data-icom={dataIcom}>
          {heading}
        </HeadingLarge>
        {!hideCreateTopicButton && organization.featureNames?.includes(Features.CreateAndEditTopics) && (
          <NewCustomTopicButton
            organizationId={organization.id}
            languages={organization.languages.map((l) => l.code)}
            defaultLanguage={organization.primaryLanguageCode}
            typeCode={createTopicTypeCode}
          />
        )}
      </div>
      <div ref={filterSectionRef} data-icom="topics-filters">
        <Box className="mt-4 mb-4 p-7 px-8">
          <TopicsFilters
            selectedTopicTypes={selectedTopicTypes}
            onTopicTypeToggle={handleTopicTypeToggle}
            onClearTopicTypes={handleClearTopicTypes}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            sortOption={sortOption}
            onSortChange={handleSortChange}
            selectedCategories={selectedCategories}
            onCategoryToggle={handleCategoryToggle}
            onClearCategories={handleClearCategories}
            selectedSurveyStatuses={selectedSurveyStatuses}
            onStatusToggle={handleStatusToggle}
            onClearStatuses={handleClearStatuses}
            availableCategories={availableCategories}
            categoryCodesToNames={categoryCodesToNames}
            variant="default"
            topicTypeCounts={topicTypeCounts}
            statusCounts={statusCounts}
            categoryCounts={categoryCounts}
            hideCategoriesInFilter={hideCategoriesInFilter}
          />
        </Box>
      </div>

      <div>
        {groupedByCategory
          ? Object.entries(groupedByCategory)
              .sort(([categoryA], [categoryB]) => {
                const orderA = getCategoryOrder(categoryA);
                const orderB = getCategoryOrder(categoryB);
                if (orderA !== orderB) {
                  return orderA - orderB;
                }
                return categoryA.localeCompare(categoryB);
              })
              .map(([category, categoryTopics]) => (
                <div key={category}>
                  <HeadingMedium className="d-flex align-items-center">
                    <CategoryIcon categoryCode={category} custom={false} style={{ width: '32px', height: '32px' }} />{' '}
                    <span className="ml-5">{categoryCodesToNames[category]}</span>
                  </HeadingMedium>
                  {categoryTopics.map((topic) => (
                    <TopicGroup
                      key={topic.id}
                      displayCategoryIcon={false}
                      user={user}
                      topicGroup={topic}
                      stepTopics={
                        data?.allowedTopicGroups?.filter(
                          (t) => topic.steps?.some((s) => s.stepTopicGroup?.id === t.id),
                        ) || []
                      }
                      organizationLanguages={organization.languages}
                      languageId={organization.primaryLanguageId}
                      showOptions={true}
                      actionButtonText={actionButtonText}
                      actionButtonOnClick={actionButtonOnClick}
                      requireValidForAction={requireValidForAction}
                      setLoading={setTopicActionLoading}
                      freeProductVersion={organization.productVersion?.product === ProductCode.Free}
                    />
                  ))}
                </div>
              ))
          : sortedTopics.map((topic) => (
              <TopicGroup
                key={topic.id}
                displayCategoryIcon={true}
                user={user}
                topicGroup={topic}
                stepTopics={
                  data?.allowedTopicGroups?.filter((t) => topic.steps?.some((s) => s.stepTopicGroup?.id === t.id)) || []
                }
                organizationLanguages={organization.languages}
                languageId={organization.primaryLanguageId}
                showOptions={true}
                actionButtonText={actionButtonText}
                actionButtonOnClick={actionButtonOnClick}
                setLoading={setTopicActionLoading}
                freeProductVersion={organization.productVersion?.product === ProductCode.Free}
              />
            ))}
      </div>
      {topicActionLoading && <Loading />}
    </PageWrapper>
  );
};
